import React, { useContext } from 'react';
import _ from 'lodash';
import getDeviceType from './getDeviceType';

export const createModelInfo = () => {
  const {
    appResources,
    UI_PATH: uiPath,
    CONTEXT_PATH: contextPath,
    AUTH_CONTEXT: auth,
    VERSION: version
  } = window;
  const {
    language,
    country,
    brand,
    brandBase,
    brandVariant,
    assetsBrand,
    carline,
    year,
    bodyStyle,
    region,
    featureGroup
  } = appResources;
  const legacyServicesPath = `${contextPath}/services`;
  const servicesPath = `${contextPath}/api/v2`;
  const modelPath = `${language}/${country}/${brand}/${carline}/${year}/${bodyStyle}`;

  const steps = _.get(appResources, 'processSteps', []).map(x => ({
    ...x,
    path: `/${x.key.toLowerCase()}`
  }));
  const urls = _.get(appResources, 'urls', {});
  const display = _.get(appResources, 'display', {});

  const isAccessoriesOpenDefault = _.get(display, 'accessoriesOpen', false);
  const isLiteRenderMode = appResources.renderMode === 'LITE';
  const isDefaultRenderMode = !isLiteRenderMode;
  const baseUrl = _.get(appResources, 'urls.baseUrl', '');
  const homepageUrl = _.get(appResources, 'urls.homepageUrl');
  const classicModel = _.get(display, 'classicModel', false);
  const deviceType = getDeviceType();
  const enableTrimFiltering = _.get(display, 'enableTrimFiltering', false);
  const enableTrimFilterGrouping = _.get(
    display,
    'enableTrimFilterGrouping',
    false
  );
  const enableTrimFilterCollapseSingleDrive = _.get(
    display,
    'enableTrimFilterCollapseSingleDrive',
    false
  );
  const enableConfigStep = steps.length > 0 && steps[0].key === 'CONFIG';
  const enableTrimCompare = !_.get(display, 'disableTrimCompare', false);
  const enablePaymentEstimator = _.get(
    display,
    'showPaymentEstimatorComponent',
    true
  );
  const enableLeasePayments = _.get(
    display,
    'paymentEstimatorComponent.lease',
    true
  );
  const enableFinancePayments = _.get(
    display,
    'paymentEstimatorComponent.finance',
    true
  );
  const enableChat = _.get(display, 'useLivePersonChat', false);
  const liveChatType = _.get(display, 'liveChatType', 'salesforce');
  const enableAuth = _.get(auth, 'enabled', false);
  const enableDimensions = _.includes(['truck', 'van'], featureGroup);

  const title = _.get(appResources, 'bodyStyleTitle', '');

  const baseVehicleShopperParamsUrl = _.get(
    appResources,
    'urls.vehicleShopperParamsUrl',
    ''
  );
  const vehicleShopperParamsUrl = `${baseVehicleShopperParamsUrl}${brandBase}/${language}_${country}/`;
  const enableVehicleShopper = _.get(display, 'enableVehicleShopper', false);

  const isFleetCA = brandVariant === 'fleet' && country === 'CA';

  const isFamilyFirstCA = brandVariant === 'ff' && country === 'CA';

  const isFamilyFirstUS =
    (brandVariant === 'ff' && country === 'US') ||
    brandVariant === 'de' ||
    brandVariant === 'co' ||
    brandVariant === 'ed' ||
    brandVariant === 'mi' ||
    brandVariant === 'su' ||
    brandVariant === 'fr';

  const isFamilyFirst = isFamilyFirstCA || isFamilyFirstUS;

  const isLNVEnabled = (isFamilyFirstCA, isFleetCA) => {
    if (isFamilyFirstCA || isFleetCA) {
      return false;
    }

    const lnv = _.get(appResources, 'callToActions').find(
      cta => cta.key === 'lnv'
    );
    if (lnv) {
      return lnv.enabled;
    }
    return true;
  };

  return {
    legacyServicesPath,
    servicesPath,
    modelPath,
    uiPath,
    version,
    language,
    country,
    brand,
    brandBase,
    brandVariant,
    assetsBrand,
    carline,
    year,
    bodyStyle,
    region,
    locale: `${language}-${country}`,
    title,
    isAccessoriesOpenDefault,
    isLiteRenderMode,
    isDefaultRenderMode,
    isFleet: brandVariant === 'fleet',
    isFamilyFirstCA,
    isFamilyFirstUS,
    isFamilyFirst,
    isCvo: brandVariant === 'cvo',
    isMobile: deviceType === 'MOBILE',
    isQuantum: _.get(appResources, 'worldPlatformMode') === 'QUANTUM',
    isCanada: country === 'CA',
    baseUrl,
    homepageUrl,
    classicModel,
    featureGroup,
    deviceType,
    enableTrimFiltering,
    enableTrimFilterGrouping,
    enableTrimFilterCollapseSingleDrive,
    enableConfigStep,
    enableTrimCompare,
    enablePaymentEstimator,
    enableFinancePayments,
    enableLeasePayments,
    enableChat,
    liveChatType,
    enablePostalCode: isDefaultRenderMode,
    enableNotifications: isDefaultRenderMode,
    enableTutorial: isDefaultRenderMode,
    enableAuth,
    enableDimensions,
    adChoicesUrl: _.get(urls, 'adChoicesUrl', ''),
    adChoicesCookiesUrl: _.get(urls, 'adChoicesCookiesUrl', ''),
    adChoicesId: _.get(appResources, 'adChoicesId', ''),
    authRedirectUrl: _.get(auth, 'redirectUrl', '/'),
    vehicleShopperParamsUrl,
    vehicleShopperCountUrl: _.get(urls, 'vehicleShopperCountUrl', ''),
    inventoryCountUrl: _.get(appResources, 'btl.serviceUrl', ''),
    defaultModelSelectorUrl: _.get(urls, 'backOutLocationUrl', '/'),
    vehicleShopperRadius: _.get(appResources, 'vehicleShopperRadius', 100),
    enableVehicleShopper: enableVehicleShopper,
    enableInventorySummaryCount: isLNVEnabled(isFamilyFirstCA, isFleetCA),
    steps
  };
};

export const ModelInfoContext = React.createContext({});

export class ModelInfoProvider extends React.Component {
  constructor(props) {
    super(props);
    this.modelInfoContext = createModelInfo();
  }

  render() {
    return (
      <ModelInfoContext.Provider value={this.modelInfoContext}>
        {this.props.children}
      </ModelInfoContext.Provider>
    );
  }
}

export const useModelInfo = () => useContext(ModelInfoContext);

export const withModelInfo = Component => {
  const ModelInfoComponent = props => {
    return (
      <ModelInfoContext.Consumer>
        {modelInfo => <Component {...props} modelInfo={modelInfo} />}
      </ModelInfoContext.Consumer>
    );
  };
  const displayName = Component.displayName || Component.name || 'Component';
  ModelInfoComponent.displayName = `${displayName}WitModelInfo`;
  return ModelInfoComponent;
};

export default ModelInfoContext;
